import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Collapse,
  Skeleton,
  Alert,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { statusConfig, getFileType } from './patrolUtils';

const PatrolEditModal = ({
  open,
  onClose,
  selectedPatrol,
  patrolEditData,
  setPatrolEditData,
  setHasEdited,
  hasEdited,
  pendingCheckpointEdits,
  isLoadingModalDetails,
  isSavingPatrol,
  onSave,
  expandedChecklistId,
  onToggleChecklist,
  editingChecklistItemId,
  checklistItemEdit,
  setChecklistItemEdit,
  onEditChecklistItem,
  onSaveChecklistItem,
  onCancelChecklistItemEdit,
  onViewFile,
  onDownloadFile,
  onDeleteFile,
  onUpload,
  readOnly,
}) => {
  const theme = useTheme();

  if (!selectedPatrol) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EditIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Edit Patrol
          </Typography>
          <Chip
            label={statusConfig[selectedPatrol.status]?.label}
            size="small"
            sx={{
              bgcolor: statusConfig[selectedPatrol.status]?.bgColor,
              color: statusConfig[selectedPatrol.status]?.color,
              fontWeight: 600,
            }}
          />
        </Box>
      </DialogTitle>
      {hasEdited && (
        <Alert severity="warning" sx={{ mx: 3, mt: 1 }}>
          Your changes are not saved
        </Alert>
      )}
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Time Fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Time"
              type="datetime-local"
              size="small"
              value={patrolEditData.startTime}
              onChange={(e) => { setPatrolEditData({ ...patrolEditData, startTime: e.target.value }); setHasEdited(true); }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="datetime-local"
              size="small"
              value={patrolEditData.endTime}
              onChange={(e) => { setPatrolEditData({ ...patrolEditData, endTime: e.target.value }); setHasEdited(true); }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Status */}
          <TextField
            select
            label="Status"
            size="small"
            value={patrolEditData.status}
            onChange={(e) => { setPatrolEditData({ ...patrolEditData, status: e.target.value }); setHasEdited(true); }}
            SelectProps={{ native: true }}
          >
            <option value="clear">Clear</option>
            <option value="warning">Warning</option>
            <option value="emergency">Emergency</option>
            <option value="incomplete">Incomplete</option>
          </TextField>

          {/* Comment */}
          <TextField
            label="Comment"
            multiline
            rows={6}
            size="small"
            value={patrolEditData.comment}
            onChange={(e) => { setPatrolEditData({ ...patrolEditData, comment: e.target.value }); setHasEdited(true); }}
            fullWidth
          />

          <Divider />

          {/* Attachments Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFileIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Attachments ({selectedPatrol.attachments?.length || 0})
                </Typography>
              </Box>
              {!readOnly && (
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  size="small"
                  color="error"
                  onClick={() => onUpload(selectedPatrol)}
                >
                  Upload
                </Button>
              )}
            </Box>
            {selectedPatrol.attachments && selectedPatrol.attachments.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedPatrol.attachments.map((file) => {
                  const fileType = getFileType(file);
                  const FileIcon = fileType === 'video' ? VideocamIcon : ImageIcon;
                  return (
                    <Box
                      key={file.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <FileIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {file.name}
                      </Typography>
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => onDownloadFile(file)}>
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {(fileType === 'image' || fileType === 'video') && (
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => onViewFile(file)}>
                            <FullscreenIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!readOnly && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => onDeleteFile(file)} color="error">
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No attachments
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Checklists Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Checklists {!isLoadingModalDetails && `(${selectedPatrol.checklists?.length || 0})`}
              </Typography>
              {isLoadingModalDetails && <CircularProgress size={16} />}
            </Box>
            {isLoadingModalDetails ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 1 }} />
                ))}
              </Box>
            ) : selectedPatrol.checklists && selectedPatrol.checklists.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedPatrol.checklists.map((checklist) => (
                  <Box key={checklist.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        borderRadius: expandedChecklistId === checklist.id ? '4px 4px 0 0' : 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                      }}
                      onClick={() => onToggleChecklist(checklist.id)}
                    >
                      {expandedChecklistId === checklist.id ? (
                        <KeyboardArrowDownIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      )}
                      <Chip
                        label={statusConfig[checklist.status]?.label || 'Clear'}
                        size="small"
                        sx={{
                          bgcolor: statusConfig[checklist.status]?.bgColor,
                          color: statusConfig[checklist.status]?.color,
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {checklist.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        {checklist.items?.length || 0} items
                      </Typography>
                    </Box>
                    <Collapse in={expandedChecklistId === checklist.id} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                          borderRadius: '0 0 4px 4px',
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          borderTop: 'none',
                        }}
                      >
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Checkpoint</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Comment</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12, width: 80 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {checklist.items?.map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell sx={{ fontSize: 12 }}>{item.checkpoint}</TableCell>
                                  <TableCell>
                                    {editingChecklistItemId === item.id ? (
                                      checklistItemEdit.statusOptions && checklistItemEdit.statusOptions.length > 0 ? (
                                        <TextField
                                          select
                                          size="small"
                                          value={checklistItemEdit.selectedStatusId || ''}
                                          onChange={(e) => setChecklistItemEdit({ ...checklistItemEdit, selectedStatusId: Number(e.target.value) })}
                                          SelectProps={{ native: true }}
                                          sx={{ minWidth: 130 }}
                                        >
                                          <option value="">Select...</option>
                                          {checklistItemEdit.statusOptions.map((statusOption) => (
                                            <option key={statusOption.StatusID} value={statusOption.StatusID}>
                                              {statusOption.Name}
                                            </option>
                                          ))}
                                        </TextField>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                          No options
                                        </Typography>
                                      )
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Chip
                                          label={item.statusOptions?.find(s => s.StatusID === item.selectedStatusId)?.Name || item.status}
                                          size="small"
                                          sx={{
                                            bgcolor: statusConfig[item.status]?.bgColor || 'rgba(158, 158, 158, 0.1)',
                                            color: statusConfig[item.status]?.color || '#9e9e9e',
                                            fontWeight: 500,
                                            fontSize: 10,
                                          }}
                                        />
                                        {pendingCheckpointEdits[item.id] && (
                                          <Box
                                            sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: '50%',
                                              bgcolor: 'warning.main',
                                            }}
                                            title="Unsaved changes"
                                          />
                                        )}
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {editingChecklistItemId === item.id ? (
                                      <TextField
                                        size="small"
                                        placeholder="Add comment..."
                                        value={checklistItemEdit.comment}
                                        onChange={(e) => setChecklistItemEdit({ ...checklistItemEdit, comment: e.target.value })}
                                        fullWidth
                                      />
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        {item.comment || '-'}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {editingChecklistItemId === item.id ? (
                                      <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Apply">
                                          <IconButton size="small" onClick={onSaveChecklistItem} color="success">
                                            <CheckIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                          <IconButton size="small" onClick={onCancelChecklistItemEdit}>
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    ) : (
                                      <Tooltip title="Edit">
                                        <IconButton
                                          size="small"
                                          onClick={() => onEditChecklistItem(item)}
                                          sx={pendingCheckpointEdits[item.id] ? { color: 'warning.main' } : {}}
                                        >
                                          <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Collapse>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No checklists
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box>
          {Object.keys(pendingCheckpointEdits).length > 0 && (
            <Typography variant="caption" color="warning.main">
              {Object.keys(pendingCheckpointEdits).length} checkpoint edit(s) pending
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isSavingPatrol}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isSavingPatrol}
            startIcon={isSavingPatrol ? <CircularProgress size={16} /> : null}
          >
            {isSavingPatrol ? 'Saving...' : 'Save All'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PatrolEditModal;
